import { trim } from 'lodash';
import forge from 'node-forge';
import { useIntl } from 'react-intl';
import { checkUserResourcePermissions, checkUserRouteAccess } from 'sonata-static-storage/lib/helpers/user.helpers';
import { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import { InfoWalletMenu } from '../InfoWalletMenu';
import { useMenu } from '../Menu.context';
import { MenuDropdown } from '../MenuDropdown';
import { NewsButtonMenu } from '../NewsButtonMenu/NewsButtonMenu';

import './userMenu.scss';

import type { CurrencyInfo } from 'sonata-static-storage/lib/samples/currencies.samples';
import type { SessionInterface } from 'sonata-static-storage/lib/schemas/session/session.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type MenuOption = {
  blank?: boolean;
  customValidation?: (user: UserInterface, session: SessionInterface) => boolean;
  defaultText?: string;
  icon?: string;
  id: string;
  onClick?: () => void;
  path?: string;
  permission?: string;
  routePermission?: string;
};

type Props = {
  currencyInfo: CurrencyInfo;
  disableChangeUser?: boolean;
  onLogout?: () => void;
  session: SessionInterface;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

function UserMenu({ currencyInfo, disableChangeUser, onLogout, session, user, userRoles }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { showAliasModal, showChangeUserModal, showMyProfileModal } = useMenu();
  const { sonataManagementAppUrl } = taptap.config;

  const checkPermission = (menuOption: MenuOption) => {
    if (menuOption.permission) {
      return checkUserResourcePermissions(menuOption.permission, user, userRoles);
    }
    if (menuOption.routePermission) {
      return checkUserRouteAccess(menuOption.routePermission, user, userRoles);
    }
    return true;
  };

  const userMenuOptions = [
    {
      defaultText: formatMessage({ defaultMessage: 'User view', id: 'menu.userMenu.changeUser' }),
      icon: 'fa-solid fa-people-roof',
      id: 'masquerade',
      onClick: disableChangeUser ? undefined : () => showChangeUserModal('show'),
      permission: 'masquerade'
    },
    {
      customValidation: (u: UserInterface, s: SessionInterface) => {
        return !s.isAlias || u.aliasFrom?.aliasConfiguration?.parentUser !== u.id;
      },
      defaultText: formatMessage({ defaultMessage: 'My profile', id: 'menu.userMenu.myProfile' }),
      icon: 'fa-regular fa-user-tie',
      id: 'myProfile',
      onClick: () => showMyProfileModal('show')
    },
    {
      blank: true,
      defaultText: formatMessage({ defaultMessage: 'Management', id: 'menu.userMenu.management' }),
      icon: 'fa-solid fa-users-gear',
      id: 'management',
      onClick: () => {
        window.open(sonataManagementAppUrl, '_blank', 'noopener,noreferrer');
      },
      routePermission: 'management'
    },
    {
      defaultText: formatMessage({ defaultMessage: 'Alias', id: 'menu.userMenu.alias' }),
      icon: 'fa-solid fa-signature',
      id: 'alias',
      onClick: () => showAliasModal('show'),
      permission: 'alias'
    },
    {
      defaultText: formatMessage({ defaultMessage: 'Logout', id: 'menu.userMenu.logout' }),
      icon: 'fas fa-sign-out',
      id: 'logout',
      isButton: true,
      onClick: onLogout
    }
  ]
    .filter(menuOption => checkPermission(menuOption) || menuOption.customValidation?.(user, session))
    .map(option => ({
      ...option,
      title: formatMessage({
        defaultMessage: option.defaultText || 'User menu option',
        id: `menu.userMenu.${option.id}`
      })
    }));

  const getUserName = () => {
    if (!session.isAlias || user.aliasFrom?.aliasConfiguration?.parentUser !== user.id) {
      return user.name || user.email.split('@')[0];
    }
    return user.aliasFrom?.name;
  };

  const getUserFromName = () => {
    if (session.isAlias || (user.aliasFrom && user.aliasFrom?.aliasConfiguration?.parentUser === user.id)) {
      return null;
    }
    let userFrom = null;
    if ((user.customUser && user.customUser.id !== user.id) || (!user.customUser && user.userFrom)) {
      const otherUser = user.customUser ?? user.userFrom;
      userFrom =
        trim(otherUser?.name).length !== 0 ? otherUser?.name : (otherUser as Record<string, any>)?.email.split('@')[0];
    }

    return userFrom ? `(${userFrom})` : null;
  };

  const getUserImage = () => {
    if (user.image?.image) {
      return `${taptap.config.staticsUrlBase}${user.image.image}`;
    }
    const md = forge.md.md5.create();
    md.update(user.email);
    const $email = md.digest().toHex();
    const encodedImageUri = encodeURIComponent(`${taptap.config.staticsUrlBase}/image/genericUser.png`);
    return `https://www.gravatar.com/avatar/${$email}?d=${encodedImageUri}`;
  };

  const userName = getUserName();
  const userImage = getUserImage();
  const userFromName = getUserFromName();

  return (
    <div className="userMenu">
      <MenuDropdown
        icon="fa-regular fa-user"
        image={userImage}
        items={userMenuOptions}
        subtitle={userFromName ?? undefined}
        title={userName as string}
      />
      <InfoWalletMenu currencyInfo={currencyInfo} user={user} />
      <NewsButtonMenu theme="light" user={user} userRoles={userRoles} />
    </div>
  );
}

export default UserMenu;
